/* eslint-disable @typescript-eslint/promise-function-async */
import * as R from "remeda";
import type { AuthTarget } from "./index";
import type { RootStore } from "../stores";
import type { EngineClient } from "../services/engineClient";
import * as T from "../stores/automation/mergers/trigger";
import * as A from "../stores/automation/mergers/actions";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const CREDENTIAL_STATUS_ENABLED = 0;

const syncOauthCredentials = async ({
  store,
  engineClient,
  solutionId,
  target,
  integrationId,
  pendingCredentialId,
}: {
  store: RootStore;
  engineClient: EngineClient;
  solutionId: string;
  target: AuthTarget;
  integrationId: string;
  pendingCredentialId: string;
}): Promise<void> => {
  if (store.config.solutionId !== solutionId) {
    throw new Error("SolutionId was changed");
  }

  const { credential: addedCredential } = await engineClient.getCredentialById({
    credentialId: pendingCredentialId,
    integrationId,
  });

  if (!R.isTruthy(addedCredential)) {
    return;
  }

  const {
    credentials,
    automations: { candidate },
  } = store;
  const actionGroupType =
    candidate.currentActionGroup?.config.oneofKind ?? "actions";

  credentials.appendItemIfNew(addedCredential);

  if (target.type === "trigger") {
    if (candidate.self.trigger.hash === target.hash) {
      candidate.setTrigger(T.mergeCredentialId(addedCredential.credentialId));
    }
  } else if (target.type === "action") {
    candidate.setActions(
      [A.mergeCredentialId(target.hash, addedCredential.credentialId)],
      actionGroupType,
    );
  }
};

const frameOAuthWindowMessageHandler =
  (store: RootStore, engineClient: EngineClient) =>
  ({ origin, data }: MessageEvent) => {
    // Turn on when you can handle it
    // if (origin !== process.env.REACT_APP_AUTH_ORIGIN) return;

    if (frameOAuthStore.getActiveAttempt() === undefined) return;

    if (["success", "paragon_success"].includes(data)) {
      void frameOAuthStore
        .closeSuccessOAuth()
        .then(({ solutionId, target, integrationId, pendingCredentialId }) =>
          syncOauthCredentials({
            store,
            engineClient,
            solutionId,
            target,
            integrationId,
            pendingCredentialId,
          }),
        );
    } else if (data === "failure") {
      frameOAuthStore.closeFailOAuth();
    }
  };

const startCredentialPolling = ({
  attempt,
  store,
  engineClient,
}: {
  attempt: OAuthAttempt;
  store: RootStore;
  engineClient: EngineClient;
}): void => {
  const finalize = (): void => {
    if (attempt.pollHandle !== undefined) {
      window.clearInterval(attempt.pollHandle);
      attempt.pollHandle = undefined;
    }
    if (attempt.timeoutHandle !== undefined) {
      window.clearTimeout(attempt.timeoutHandle);
      attempt.timeoutHandle = undefined;
    }
  };

  attempt.pollHandle = window.setInterval(() => {
    void (async () => {
      if (frameOAuthStore.list[0] !== attempt) {
        finalize();
        return;
      }

      try {
        const { credential } = await engineClient.getCredentialById({
          credentialId: attempt.pendingCredentialId,
          integrationId: attempt.integrationId,
        });

        if (
          R.isTruthy(credential) &&
          credential.status === CREDENTIAL_STATUS_ENABLED &&
          frameOAuthStore.list[0] === attempt
        ) {
          finalize();
          window.removeEventListener("message", attempt.listener);
          try {
            attempt.oauthWindow.close();
          } catch {
            /* ignore — popup may already be gone */
          }
          frameOAuthStore.list = [];

          await syncOauthCredentials({
            store,
            engineClient,
            solutionId: attempt.solutionId,
            target: attempt.target,
            integrationId: attempt.integrationId,
            pendingCredentialId: attempt.pendingCredentialId,
          });
        }
      } catch {
        // Transient errors are swallowed — the next tick will retry.
      }
    })();
  }, POLL_INTERVAL_MS) as unknown as number;

  attempt.timeoutHandle = window.setTimeout(() => {
    if (frameOAuthStore.list[0] === attempt) {
      finalize();
      window.removeEventListener("message", attempt.listener);
      try {
        attempt.oauthWindow.close();
      } catch {
        /* ignore */
      }
      frameOAuthStore.list = [];
    } else {
      finalize();
    }
  }, POLL_TIMEOUT_MS) as unknown as number;
};

class FrameOAuthStore {
  list: OAuthAttempt[] = [];

  startOAuth(
    oauthWindow: Window,
    solutionId: string,
    target: AuthTarget,
    integrationId: string,
    pendingCredentialId: string,
    rootStore: RootStore,
    engineClient: EngineClient,
  ): void {
    if (this.getActiveAttempt() !== undefined) return;

    const listener = frameOAuthWindowMessageHandler(rootStore, engineClient);
    window.addEventListener("message", listener);
    const attempt: OAuthAttempt = {
      oauthWindow,
      solutionId,
      target,
      integrationId,
      pendingCredentialId,
      listener,
      pollHandle: undefined,
      timeoutHandle: undefined,
    };
    this.list = [attempt];

    startCredentialPolling({ attempt, store: rootStore, engineClient });
  }

  getActiveAttempt(): OAuthAttempt | undefined {
    const attempt = this.list[0];
    if (attempt === undefined) return undefined;
    if (attempt.oauthWindow.closed) {
      this.clearTimers(attempt);
      window.removeEventListener("message", attempt.listener);
      this.list = [];
      return undefined;
    }
    return attempt;
  }

  closeSuccessOAuth(): Promise<{
    solutionId: string;
    target: AuthTarget;
    integrationId: string;
    pendingCredentialId: string;
  }> {
    return new Promise((resolve, reject) => {
      const attempt = this.list[0];
      if (attempt === undefined) {
        reject(new Error("OAuthAttempt list is empty"));
        return;
      }
      this.clearTimers(attempt);
      window.removeEventListener("message", attempt.listener);
      attempt.oauthWindow.close();
      this.list = [];

      resolve({
        solutionId: attempt.solutionId,
        target: attempt.target,
        integrationId: attempt.integrationId,
        pendingCredentialId: attempt.pendingCredentialId,
      });
    });
  }

  closeFailOAuth(): void {
    const attempt = this.list[0];
    if (attempt === undefined) return;
    this.clearTimers(attempt);
    window.removeEventListener("message", attempt.listener);
    attempt.oauthWindow.close();
    this.list = [];
  }

  private clearTimers(attempt: OAuthAttempt): void {
    if (attempt.pollHandle !== undefined) {
      window.clearInterval(attempt.pollHandle);
      attempt.pollHandle = undefined;
    }
    if (attempt.timeoutHandle !== undefined) {
      window.clearTimeout(attempt.timeoutHandle);
      attempt.timeoutHandle = undefined;
    }
  }
}

interface OAuthAttempt {
  oauthWindow: Window;
  solutionId: string;
  target: AuthTarget;
  integrationId: string;
  pendingCredentialId: string;
  listener: ReturnType<typeof frameOAuthWindowMessageHandler>;
  pollHandle: number | undefined;
  timeoutHandle: number | undefined;
}

export const frameOAuthStore = new FrameOAuthStore();
